import { storageService } from '../storage';
import {
    DEFAULT_CURRENCY,
    DEFAULT_LOCALE,
    SELECTED_CURRENCY_STORAGE_KEY,
    SELECTED_GAME_LOCALE_STORAGE_KEY,
    SELECTED_LOCALE_STORAGE_KEY,
} from './constants';

type LocaleListener = (locale: string) => void;

class LauncherPreferencesService {
    private readonly localeListeners = new Set<LocaleListener>();
    private readonly gameLocaleListeners = new Set<LocaleListener>();

    public getLocale(): string {
        return this.getOrSetDefault(SELECTED_LOCALE_STORAGE_KEY, DEFAULT_LOCALE);
    }

    public setLocale(locale: string): void {
        if (locale === this.getLocale()) {
            return;
        }

        storageService.set(SELECTED_LOCALE_STORAGE_KEY, locale);
        this.notify(this.localeListeners, locale);
    }

    public subscribeLocale(listener: LocaleListener): () => void {
        return this.subscribe(this.localeListeners, listener);
    }

    public getGameLocale(): string {
        const storedValue = storageService.get<string>(SELECTED_GAME_LOCALE_STORAGE_KEY);
        return storedValue || this.getLocale();
    }

    public setGameLocale(locale: string): void {
        const storedValue = storageService.get<string>(SELECTED_GAME_LOCALE_STORAGE_KEY);
        if (storedValue === locale) {
            return;
        }

        storageService.set(SELECTED_GAME_LOCALE_STORAGE_KEY, locale);
        this.notify(this.gameLocaleListeners, locale);
    }

    public subscribeGameLocale(listener: LocaleListener): () => void {
        return this.subscribe(this.gameLocaleListeners, listener);
    }

    public getCurrency(): string {
        return this.getOrSetDefault(SELECTED_CURRENCY_STORAGE_KEY, DEFAULT_CURRENCY);
    }

    public setCurrency(currency: string): void {
        storageService.set(SELECTED_CURRENCY_STORAGE_KEY, currency);
    }

    private subscribe(
        listeners: Set<LocaleListener>,
        listener: LocaleListener
    ): () => void {
        listeners.add(listener);
        return () => {
            listeners.delete(listener);
        };
    }

    private notify(listeners: Set<LocaleListener>, locale: string): void {
        listeners.forEach((listener) => listener(locale));
    }

    private getOrSetDefault(key: string, defaultValue: string): string {
        const storedValue = storageService.get<string>(key);
        if (storedValue) {
            return storedValue;
        }

        storageService.set(key, defaultValue);
        return defaultValue;
    }
}

export const launcherPreferencesService = new LauncherPreferencesService();
